import React, { useEffect, useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState, RootDispatch } from "../../../store";
import { IWarehouseRawMaterialResponse } from "../../../models/warehouseRawMaterial.model";
import Select from "react-select";
import debounce from "lodash/debounce";
import PaginationControls from "../../../utils/PaginationControls";
import {
  Factory,
  Search,
  Clock,
  AlertTriangle,
  X,
  Loader2,
  Box,
} from "lucide-react";
import {
  getFactories,
  getStoreStock,
} from "../../../store/slices/Factory/newStoreSlice";

const NewStoreStock: React.FC = () => {
  const dispatch = useDispatch<RootDispatch>();
  const rawMaterialsByWarehouse = useSelector(
    (state: RootState) => state.newStore.rawMaterialsByWarehouse,
  );
  const pagination = useSelector(
    (state: RootState) => state.newStore.pagination.getFloorStockPagination,
  );
  const inwardLoading = useSelector(
    (state: RootState) => state.newStore.loading.getStoreStockLoading,
  );

  const factories = useSelector(
    (state: RootState) => state.newStore.warehouses,
  );

  const { loading: factoryLoading } = useSelector(
    (state: RootState) => state.warehouse,
  );

  const factoryAssigned = useSelector(
    (state: RootState) => state.auth.userName?.warehouseAssigned,
  );

  const [searchTerms, setSearchTerms] = useState<{
    [factoryId: string]: string;
  }>({});
  const [selectedFactory, setSelectedFactory] = useState(factoryAssigned || "");

  // Initial fetch for assigned factory
  useEffect(() => {
    if (factories.length <= 0) {
      dispatch(getFactories({}));
    }
    if (factoryAssigned) {
      dispatch(
        getStoreStock({
          params: {
            warehouseId: factoryAssigned,
            page: 1,
            limit: 10,
          },
        }),
      );
    }
  }, [dispatch, factoryAssigned]);

  // Fetch when factory selection changes
  useEffect(() => {
    if (selectedFactory !== "" && !rawMaterialsByWarehouse[selectedFactory]) {
      dispatch(
        getStoreStock({
          params: {
            warehouseId: selectedFactory,
            page: 1,
            limit: 10,
          },
        }),
      );
    }
  }, [selectedFactory, rawMaterialsByWarehouse, dispatch]);

  const debouncedSearch = useCallback(
    (factoryId: string, search: string) => {
      const debounced = debounce(() => {
        dispatch(
          getStoreStock({
            params: {
              warehouseId: factoryId,
              page: 1,
              limit: 10,
              search,
            },
          }),
        );
      }, 500);
      debounced();
      return debounced;
    },
    [dispatch],
  );

  const handleSearchChange = (value: string) => {
    setSearchTerms((prev) => ({ ...prev, [selectedFactory]: value }));
    debouncedSearch(selectedFactory, value);
  };

  const clearSearch = () => {
    setSearchTerms((prev) => ({ ...prev, [selectedFactory]: "" }));
    debouncedSearch(selectedFactory, "");
  };

  const handlePageChange = (page: number) => {
    const search = searchTerms[selectedFactory] || "";
    dispatch(
      getStoreStock({
        params: {
          warehouseId: selectedFactory,
          page,
          limit: 10,
          search,
        },
      }),
    );
  };

  const factoryData = selectedFactory
    ? rawMaterialsByWarehouse[selectedFactory]
    : null;
  const isLoading = inwardLoading || factoryLoading;

  return (
    <div className="min-h-screen bg-slate-50/50 p-3 sm:p-6 lg:p-8 space-y-6 max-w-7xl mx-auto">
      {/* --- Top Bar / Factory Selector --- */}
      <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-sm border border-slate-200/80 transition-all">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-blue-50 text-blue-600 rounded-xl">
              <Factory className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-slate-900 leading-tight">
                Factory Live Stock
              </h1>
              <p className="text-xs text-slate-500">
                Monitor raw materials, usage, and stock availability
              </p>
            </div>
          </div>

          {/* Select Factory Dropdown */}
          <div className="w-full sm:w-72">
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
              Select Active Factory
            </label>
            <Select
              options={factories.map((f: { _id: string; name?: string }) => ({
                value: f._id,
                label: f.name ?? "",
              }))}
              value={
                selectedFactory
                  ? {
                      value: selectedFactory,
                      label: factories.find(
                        (f: { _id: string }) => f._id === selectedFactory,
                      )?.name,
                    }
                  : null
              }
              onChange={(option) =>
                setSelectedFactory(option ? option.value : "")
              }
              placeholder="Choose a factory..."
              isSearchable
              className="text-sm"
              styles={{
                control: (base, state) => ({
                  ...base,
                  borderColor: state.isFocused ? "#3b82f6" : "#e2e8f0",
                  borderRadius: "0.75rem",
                  padding: "2px",
                  boxShadow: state.isFocused
                    ? "0 0 0 3px rgba(59, 130, 246, 0.15)"
                    : "none",
                  "&:hover": { borderColor: "#cbd5e1" },
                }),
                menu: (base) => ({
                  ...base,
                  borderRadius: "0.75rem",
                  overflow: "hidden",
                  boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
                  zIndex: 30,
                }),
              }}
            />
          </div>
        </div>
      </div>

      {/* --- Main Content Area --- */}
      {selectedFactory && factoryData ? (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200/80 overflow-hidden">
          {/* Header & Search Filter */}
          <div className="p-4 sm:p-6 border-b border-slate-100 flex flex-col sm:flex-row gap-4 items-stretch sm:items-center justify-between bg-slate-50/50">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse"></span>
              <h2 className="text-base sm:text-lg font-semibold text-slate-800">
                {factoryData.warehouseName} Inventory
              </h2>
              <span className="text-xs px-2.5 py-0.5 rounded-full bg-slate-200/70 text-slate-600 font-medium ml-1">
                {pagination?.totalCount || 0} items
              </span>
            </div>

            {/* Mobile-Friendly Search Bar */}
            <div className="relative w-full sm:w-80">
              <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search material name or SKU..."
                value={searchTerms[selectedFactory] || ""}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="w-full pl-10 pr-9 py-2 text-sm bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all text-slate-800 placeholder-slate-400"
              />
              {searchTerms[selectedFactory] && (
                <button
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Loading Indicator Overlay/State */}
          {isLoading ? (
            <div className="p-12 flex flex-col items-center justify-center text-slate-400">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600 mb-3" />
              <p className="text-sm font-medium text-slate-600">
                Fetching live inventory...
              </p>
            </div>
          ) : factoryData.rawMaterials &&
            factoryData.rawMaterials.length > 0 ? (
            <>
              {/* --- DESKTOP TABLE VIEW (Hidden on Mobile) --- */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-slate-100 text-[11px] font-bold text-slate-400 uppercase tracking-wider bg-slate-50/50">
                      <th className="py-3.5 px-6">Material Details</th>
                      <th className="py-3.5 px-4">Category</th>
                      <th className="py-3.5 px-4">Current Stock</th>
                      <th className="py-3.5 px-4">Daily Avg (7D)</th>
                      <th className="py-3.5 px-4">Stock Coverage</th>
                      <th className="py-3.5 px-6 text-right">Procurement</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-sm">
                    {factoryData.rawMaterials.map(
                      (rm: IWarehouseRawMaterialResponse) => {
                        const isLowStock =
                          rm.daysStockLeft !== null &&
                          rm.daysStockLeft <= rm.approxDeliveryDays;

                        return (
                          <tr
                            key={rm.id}
                            className="hover:bg-slate-50/80 transition-colors group"
                          >
                            {/* Name & SKU */}
                            <td className="py-4 px-6">
                              <div className="font-semibold text-slate-800 group-hover:text-blue-600 transition-colors">
                                {rm.name}
                              </div>
                              <div className="text-xs text-slate-400 font-mono mt-0.5">
                                SKU: {rm.sku}
                              </div>
                            </td>

                            {/* Type */}
                            <td className="py-4 px-4">
                              <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-slate-100 text-slate-600 border border-slate-200/60">
                                {rm.type || "N/A"}
                              </span>
                            </td>

                            {/* Stock Quantity */}
                            <td className="py-4 px-4">
                              <div className="font-bold text-slate-900">
                                {rm.quantity.toLocaleString(undefined, {
                                  minimumFractionDigits: 0,
                                  maximumFractionDigits: 2,
                                })}{" "}
                                <span className="text-xs font-normal text-slate-500">
                                  {rm.unit}
                                </span>
                              </div>
                            </td>

                            {/* 7 Days Daily Average Usage */}
                            <td className="py-4 px-4 text-slate-600">
                              {rm.lastSevenDaysAverage != null ? (
                                <span>
                                  {rm.lastSevenDaysAverage.toFixed(2)}{" "}
                                  <span className="text-xs text-slate-400">
                                    {rm.unit}/day
                                  </span>
                                </span>
                              ) : (
                                <span className="text-slate-300">—</span>
                              )}
                            </td>

                            {/* Days Stock Left / Status */}
                            <td className="py-4 px-4">
                              {rm.daysStockLeft != null ? (
                                <div className="flex items-center gap-1.5">
                                  <span
                                    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${
                                      isLowStock
                                        ? "bg-amber-50 text-amber-700 border border-amber-200"
                                        : "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                    }`}
                                  >
                                    {isLowStock && (
                                      <AlertTriangle className="w-3 h-3" />
                                    )}
                                    {Math.round(rm.daysStockLeft)} Days left
                                  </span>
                                </div>
                              ) : (
                                <span className="text-xs text-slate-400">
                                  N/A
                                </span>
                              )}
                            </td>

                            {/* Delivery Lead Time */}
                            <td className="py-4 px-6 text-right">
                              <span className="inline-flex items-center gap-1 text-xs text-slate-500 font-medium">
                                <Clock className="w-3.5 h-3.5 text-slate-400" />
                                {rm.approxDeliveryDays}d lead time
                              </span>
                            </td>
                          </tr>
                        );
                      },
                    )}
                  </tbody>
                </table>
              </div>

              {/* --- MOBILE CARD VIEW (Visible only on Mobile Screens) --- */}
              <div className="grid grid-cols-1 gap-3 p-4 md:hidden">
                {factoryData.rawMaterials.map(
                  (rm: IWarehouseRawMaterialResponse) => {
                    const isLowStock =
                      rm.daysStockLeft !== null &&
                      rm.daysStockLeft <= rm.approxDeliveryDays;

                    return (
                      <div
                        key={rm.id}
                        className="p-4 bg-white border border-slate-200 rounded-xl space-y-3 shadow-xs"
                      >
                        {/* Top Row: Name, SKU, Category */}
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <h3 className="font-bold text-slate-900 text-base leading-snug">
                              {rm.name}
                            </h3>
                            <p className="text-xs text-slate-400 font-mono">
                              SKU: {rm.sku}
                            </p>
                          </div>
                          <span className="shrink-0 px-2.5 py-0.5 rounded-md text-[11px] font-medium bg-slate-100 text-slate-600 border border-slate-200/50">
                            {rm.type}
                          </span>
                        </div>

                        {/* Stock Info Bar */}
                        <div className="p-3 bg-slate-50 rounded-lg grid grid-cols-2 gap-2 text-xs">
                          <div>
                            <span className="text-slate-400 block mb-0.5">
                              In Stock
                            </span>
                            <span className="text-sm font-bold text-slate-900">
                              {rm.quantity.toFixed(2)} {rm.unit}
                            </span>
                          </div>
                          <div>
                            <span className="text-slate-400 block mb-0.5">
                              Daily Avg
                            </span>
                            <span className="text-sm font-semibold text-slate-700">
                              {rm.lastSevenDaysAverage
                                ? `${rm.lastSevenDaysAverage.toFixed(2)} ${rm.unit}`
                                : "0"}
                            </span>
                          </div>
                        </div>

                        {/* Bottom Row: Days Left & Lead Time */}
                        <div className="flex items-center justify-between text-xs pt-1">
                          {rm.daysStockLeft != null ? (
                            <span
                              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${
                                isLowStock
                                  ? "bg-amber-50 text-amber-700 border border-amber-200"
                                  : "bg-emerald-50 text-emerald-700 border border-emerald-200"
                              }`}
                            >
                              {isLowStock && (
                                <AlertTriangle className="w-3 h-3" />
                              )}
                              {Math.round(rm.daysStockLeft)} Days Stock
                            </span>
                          ) : (
                            <div></div>
                          )}

                          <div className="flex items-center gap-1 text-slate-500 font-medium">
                            <Clock className="w-3.5 h-3.5 text-slate-400" />
                            <span>{rm.approxDeliveryDays} days lead time</span>
                          </div>
                        </div>
                      </div>
                    );
                  },
                )}
              </div>

              {/* Pagination Controls */}
              {pagination && (
                <div className="p-4 border-t border-slate-100 bg-slate-50/50">
                  <PaginationControls
                    currentPage={pagination.page}
                    totalPages={pagination.totalPages}
                    totalItems={pagination.totalCount}
                    onPageChange={handlePageChange}
                    isLoading={inwardLoading}
                  />
                </div>
              )}
            </>
          ) : (
            /* --- EMPTY STATE --- */
            <div className="py-16 px-4 text-center">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-400">
                <Box className="w-8 h-8" />
              </div>
              <h3 className="text-base font-bold text-slate-800">
                No Raw Materials Found
              </h3>
              <p className="text-sm text-slate-500 max-w-sm mx-auto mt-1">
                There are no materials recorded in stock for this factory, or
                your search term did not match any inventory.
              </p>
            </div>
          )}
        </div>
      ) : (
        /* --- PROMPT STATE (When no factory is chosen) --- */
        <div className="bg-white rounded-2xl p-12 text-center border border-slate-200/80 shadow-sm">
          <div className="w-14 h-14 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Factory className="w-7 h-7" />
          </div>
          <h3 className="text-lg font-bold text-slate-800">
            No Factory Selected
          </h3>
          <p className="text-sm text-slate-500 max-w-sm mx-auto mt-1">
            Please choose a factory from the dropdown menu above to view its
            live stock and raw material statistics.
          </p>
        </div>
      )}
    </div>
  );
};

export default NewStoreStock;
